import dynamic from 'next/dynamic';

import { merchandisingLabels } from '@/core/tamagoshi/MerchandisingTag/interfaces';
import { isB2B } from '@/domains/core/settings/utils';
import {
  Label,
  LabelVariant,
} from '@/domains/core/tamagoshiTailwind/components/Label/Label';
import { TrendDownIcon } from '@/domains/core/tamagoshiTailwind/icons/TrendDownIcon';
import { TamagoshiIconComponent } from '@/domains/core/tamagoshiTailwind/interfaces';
import { PromoTags } from '@/domains/productDiscovery/CommercialAnimation/interfaces/tags';
import { getTags } from '@/domains/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import {
  blackFridayLabel,
  loweredPriceLabel,
  manomanoDaysLabel,
  salesLabel,
} from '@/domains/productDiscovery/ProductCard/translations';
import { promoTag } from '@/domains/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PromoBlock/translations';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

const B2BPriceAdvantage = dynamic(() =>
  import(
    '@/domains/productDiscovery/ProductPage/common/components/ProductLabels/components/B2BPriceAdvantage/B2BPriceAdvantage'
  ).then((mod) => mod.B2BPriceAdvantage),
);

type PromoConfig = {
  [key in PromoTags]: {
    variant: LabelVariant;
    label: string;
    icon: TamagoshiIconComponent | undefined;
  };
};

export const ProductPromoLabels = () => {
  const { price, commercialAnimation } = useDetail();

  const promoTags = getTags(
    price?.primaryPrice.amountWithVat,
    price?.retailPrice?.amountWithVat ?? 0,
    commercialAnimation?.tags,
  );

  // Higher in the array means higher priority
  const promoPriority = [
    merchandisingLabels.LOWERED_PRICE,
    merchandisingLabels.MANOMANODAYS,
    merchandisingLabels.SALES,
    merchandisingLabels.BLACKFRIDAY,
    merchandisingLabels.PROMO,
  ];

  // Get the first tag that matches the priority
  const promoType = promoPriority.find((label) =>
    promoTags.includes(label as PromoTags),
  );

  const promoConfig: PromoConfig = {
    [merchandisingLabels.BLACKFRIDAY]: {
      variant: 'FoundationQuinary',
      label: blackFridayLabel,
      icon: undefined,
    },
    [merchandisingLabels.SALES]: {
      variant: 'PromotionPrimary',
      label: salesLabel,
      icon: undefined,
    },
    [merchandisingLabels.MANOMANODAYS]: {
      variant: 'PromotionPrimary',
      label: manomanoDaysLabel,
      icon: undefined,
    },
    [merchandisingLabels.LOWERED_PRICE]: {
      variant: 'PromotionPrimary',
      label: loweredPriceLabel,
      icon: TrendDownIcon,
    },
    [merchandisingLabels.PROMO]: {
      variant: 'PromotionPrimary',
      label: promoTag,
      icon: undefined,
    },
  };

  const promo = promoConfig[promoType as PromoTags];

  if (promo) {
    return (
      <>
        <Label text={promo.label} variant={promo.variant} icon={promo.icon} />
        {commercialAnimation?.retailPriceTimeLeftLabel && (
          <span>{commercialAnimation.retailPriceTimeLeftLabel}</span>
        )}
      </>
    );
  }

  return isB2B() ? <B2BPriceAdvantage /> : null;
};

ProductPromoLabels.displayName = 'ProductPromoLabels';
